import { useState } from "react";
import { useToast } from "@/components/ux/Toast";
import { useT } from "@/lib/i18n";
import { useEventCoverUpload } from "@/lib/api/useEventCoverUpload";

export const MAX_POST_LENGTH = 5000; // ClubAPI.CreatePostRequestSchema
const MAX_IMAGES = 4;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** Post body as stored: the formatted text, then the attached photos as markdown images. */
export function composeBody(text: string, images: string[]) {
  return [text.trim(), images.map((url) => `![](${url})`).join("\n")].filter(Boolean).join("\n\n");
}

/** Photo attachments for a forum post: upload, remove, reset. */
export function usePostAttachments() {
  const { t } = useT();
  const toast = useToast();
  const upload = useEventCoverUpload();
  const [images, setImages] = useState<string[]>([]);

  const add = async (files: FileList) => {
    for (const file of Array.from(files).slice(0, MAX_IMAGES - images.length)) {
      if (!file.type.startsWith("image/")) continue;
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(t("clubs.imageTooLarge"));
        continue;
      }
      try {
        const url = await upload.mutateAsync(file);
        setImages((prev) => [...prev, url]);
      } catch (err) {
        toast.error((err as Error).message || t("clubs.uploadFailed"));
      }
    }
  };

  return {
    images,
    uploading: upload.isPending,
    canAdd: images.length < MAX_IMAGES && !upload.isPending,
    add,
    remove: (url: string) => setImages((prev) => prev.filter((u) => u !== url)),
    reset: () => setImages([]),
  };
}

export type PostAttachments = ReturnType<typeof usePostAttachments>;
